import { useQuery } from "@apollo/client";
import { notify, notifyError } from "../../../../utils/toast";
import { getDownloadLink } from "../../../../utils/app-download-link";
import { ME_QUERY } from "../../../../api/user-queries";
import { useAuth } from "../../../../core/auth";
import Header from "../../../../components/common/header";
import { LuCopy } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { track } from "@amplitude/analytics-browser";

const InviteIntro = () => {
  const { data } = useQuery(ME_QUERY);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const copyLink = () => {
    track("초대하기 클릭", { 어디서: "친구 초대 이벤트" });

    const downLoadLink = getDownloadLink();
    const message = `히어로님 안녕하세요 마감히어로에 합류해주세요!\n\n당신을 히어로로 초대합니다!\n\n내 초대 코드: ${data?.me?.referralCode}\n앱 다운로드 링크 👉 ${downLoadLink}`;

    navigator.clipboard
      .writeText(message)
      .then(() => {
        notify("앱 초대 링크가 복사되었습니다!");
      })
      .catch((err) => {
        notifyError("앱 초대 링크 복사 실패:" + err);
      });
  };

  const copyReferralCode = () => {
    track("추천인 코드 복사", { 어디서: "친구 초대 이벤트" });
    if (data?.me?.referralCode) {
      navigator.clipboard.writeText(data.me.referralCode);
      notify("추천인 코드가 복사되었습니다.");
    }
  };

  const handleReviewInviteClick = () => {
    if (isLoggedIn) {
      track("내가 쓴 찐후기로 초대하기 클릭", { 어디서: "친구 초대 이벤트" });
      navigate("/my/review");
    } else {
      notifyError("로그인 후 이용해주세요.");
    }
  };

  return (
    <div className="w-full h-screen overflow-y-scroll bg-white">
      <Header title="친구 초대 이벤트" showBackButton />

      <div className="px-4 mt-4">
        <div className="border rounded-2xl p-6 mb-8 shadow-md bg-primary">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold mb-3">
              {isLoggedIn ? (
                <>
                  <span className="font-bold bg-white rounded-full px-3 py-1 text-primary text-lg">
                    {data?.me?.name || "히어로"}
                  </span>
                  <span className="text-white ml-2 text-lg">
                    님의 레퍼럴 코드
                  </span>
                </>
              ) : (
                <span className="text-white text-lg">당신의 레퍼럴 코드</span>
              )}
            </h2>
            <div className="relative bg-white/10 rounded-xl p-4 mb-4">
              <p className="text-5xl font-bold text-white tracking-wider">
                {isLoggedIn ? data?.me?.referralCode : "?????"}
              </p>
              <button
                onClick={() => copyReferralCode()}
                className="absolute right-[5%] top-1/2 -translate-y-1/2 flex items-center gap-2 bg-white text-black p-1.5 rounded-lg font-semibold text-sm"
              >
                <LuCopy size={16} className="text-gray-900" />
              </button>
            </div>
            {!isLoggedIn && (
              <p className="text-white/90 text-md font-bold">
                로그인 후 확인 가능합니다.
              </p>
            )}
          </div>

          <div className="flex flex-wrap w-full gap-2">
            <button
              onClick={() => copyLink()}
              className="flex-4 bg-gray-50 text-gray-800 py-2.5 px-3 rounded-lg font-medium text-md"
            >
              초대하기
            </button>
            <button
              onClick={() => handleReviewInviteClick()}
              className="flex-1 bg-white text-black py-2.5 px-4 rounded-lg font-bold"
            >
              내가 쓴 찐후기로 초대하기
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8">
          <div className="text-center mb-4">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              친구 초대하면?
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <div className="text-3xl font-black text-primary mb-2">
                <span className="text-[#1562fc] text-2xl">1,000원</span>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-md font-bold text-primary rounded-full px-2 py-0.5 border border-primary">
                  친구 쿠폰
                </div>
              </div>
              <div className="font-medium text-gray-500 mt-1">
                가입 즉시 지급
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <div className="text-3xl font-black text-primary mb-2">
                <span className="text-[#1562fc] text-2xl">1,000원</span>
              </div>
              <div className="flex items-center justify-center">
                <div className="text-md font-bold text-primary rounded-full px-2 py-0.5 border border-primary">
                  내 쿠폰
                </div>
              </div>
              <div className="font-medium text-gray-500 mt-1">
                친구가 픽업 완료 시
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-md">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-800">친구 초대하기</h3>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              친구에게 내 추천인 코드를 공유하고 회원가입을 도와주세요
            </p>
            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <span className="text-green-600 text-lg">🎁</span>
                <span className="text-green-700 font-semibold">
                  친구에게 <span className="text-[#1562fc]">1,000원</span> 쿠폰
                  즉시 지급!
                </span>
              </div>
              <ul className="mt-2 space-y-0.5 text-sm text-gray-500">
                <li>발급일 기준 1일 이내 사용 가능합니다.</li>
                <li>최소 주문: 4,900원 이상</li>
              </ul>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-md">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-800">친구 첫 주문</h3>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              친구가 첫 주문을 완료하고 픽업까지 마친다면?
            </p>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <span className="text-blue-600 text-lg">💝</span>
                <span className="text-blue-700 font-semibold">
                  나에게 <span className="text-[#1562fc]">1,000원</span> 쿠폰
                  지급!
                </span>
              </div>
              <ul className="mt-2 space-y-0.5 text-sm text-gray-500">
                <li>발급일 기준 7일 이내 사용 가능합니다.</li>
                <li>최소 주문: 4,900원 이상</li>
              </ul>
            </div>
          </div>

          <div className="p-4"></div>
        </div>
      </div>
    </div>
  );
};

export default InviteIntro;
